import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import driverApi from "../../../apis/driverApi";
import donationApi from "../../../apis/donationApi";
import Loading from "../../Loading/Loading";
import ModalCreateDriver from "../Modal/ModalCreateDriver";
import ModalAssignDistrict from "../Modal/ModalAssignDistrict";
import DISTRICTS from "../../../constants/district";

const DriverList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [drivers, setDrivers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [districtModalOpen, setDistrictModalOpen] = useState(false);
  const [selectedDriverForDistrict, setSelectedDriverForDistrict] = useState<any>(null);
  const [driverDistrictsMap, setDriverDistrictsMap] = useState<{ [driverId: string]: string[] }>({});

  const loadDriverDistricts = () => {
    try {
      const stored = localStorage.getItem("driver_assigned_districts");
      if (stored) {
        setDriverDistrictsMap(JSON.parse(stored));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveDistricts = async (driverId: string, districtCodes: string[]) => {
    try {
      const oldDistrictsForThisDriver = driverDistrictsMap[driverId] || [];
      const removedDistricts = oldDistrictsForThisDriver.filter((code) => !districtCodes.includes(code));

      const updated = { ...driverDistrictsMap };
      // Ensure exclusivity: remove these districtCodes from any other driver
      Object.keys(updated).forEach((otherId) => {
        if (otherId !== driverId) {
          updated[otherId] = (updated[otherId] || []).filter(
            (code) => !districtCodes.includes(code)
          );
        }
      });
      updated[driverId] = districtCodes;
      setDriverDistrictsMap(updated);
      localStorage.setItem("driver_assigned_districts", JSON.stringify(updated));
      enqueueSnackbar("Đã lưu khu vực. Đang đồng bộ và chuyển giao đơn hàng...", { variant: "info" });

      (async () => {
        let assignedCount = 0;
        let unassignedCount = 0;

        // 1. For removed districts: unassign active orders that belonged to this driver
        for (const rCode of removedDistricts) {
          try {
            const res = await donationApi.getDonations({
              filter: {
                provinceId: "202",
                shippingTypeId: "11123",
                statuses: ["new", "confirm", "processing"],
                districtId: rCode,
              },
              paging: { offset: 0, limit: 100 },
            });
            if (res.data?.code === 0 && res.data?.data?.userDonations) {
              for (const order of res.data.data.userDonations) {
                if (order.driverId === driverId) {
                  await donationApi.assignDriver({
                    orderId: order.id,
                    driverId: "",
                    driverIncome: 0,
                  });
                  unassignedCount++;
                }
              }
            }
          } catch (e) {
            console.error("Error unassigning removed district", rCode, e);
          }
        }

        // 2. For newly assigned districts: reassign active orders in these districts to this driver
        for (const dCode of districtCodes) {
          try {
            const res = await donationApi.getDonations({
              filter: {
                provinceId: "202",
                shippingTypeId: "11123",
                statuses: ["new", "confirm", "processing"],
                districtId: dCode,
              },
              paging: { offset: 0, limit: 100 },
            });
            if (res.data?.code === 0 && res.data?.data?.userDonations) {
              const orders = res.data.data.userDonations;
              for (const order of orders) {
                // If order has no driver OR is assigned to someone else (or deleted driver)
                if (order.driverId !== driverId) {
                  await donationApi.assignDriver({
                    orderId: order.id,
                    driverId: driverId,
                    driverIncome: order.driverIncome || 0,
                  });
                  assignedCount++;
                }
              }
            }
          } catch (err) {
            console.error(`Auto assign error for district ${dCode}`, err);
          }
        }

        let msg = "Đã cập nhật khu vực phụ trách cho tài xế!";
        if (assignedCount > 0 && unassignedCount > 0) {
          msg = `Đã chuyển giao ${assignedCount} đơn sang tài xế và gỡ ${unassignedCount} đơn khỏi khu vực cũ!`;
        } else if (assignedCount > 0) {
          msg = `Đã tự động chuyển giao ${assignedCount} đơn hàng cho tài xế này!`;
        } else if (unassignedCount > 0) {
          msg = `Đã gỡ gán ${unassignedCount} đơn hàng thuộc các quận vừa bỏ chọn!`;
        }
        enqueueSnackbar(msg, { variant: "success" });
      })();
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Lỗi khi lưu khu vực", { variant: "error" });
    }
  };

  const getPartnerId = (): string => {
    return process.env.REACT_APP_ENV === "production" ? "01G486CQAV7VW2FK3DHZV1T2FG" : "01G4866BBJGYR1JH8E6RPYEW2C";
  };

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const res = await driverApi.listDrivers(getPartnerId());
      if (res.data.code === 0) {
        setDrivers(res.data.data || []);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Lỗi khi lấy danh sách tài xế", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
    loadDriverDistricts();
  }, []);

  const handleToggleStatus = async (driver: any) => {
    const nextStatus = driver.status === "DISABLED" ? "ACTIVE" : "DISABLED";
    const actionText = nextStatus === "DISABLED" ? "tạm dừng" : "kích hoạt lại";
    
    if (!window.confirm(`Bạn có chắc chắn muốn ${actionText} tài xế "${driver.name}"?`)) {
      return;
    }

    try {
      // Synchronize in local state
      setDrivers((prev) =>
        prev.map((d) => (d.id === driver.id ? { ...d, status: nextStatus } : d))
      );

      // Call backend API
      try {
        await driverApi.updateStatus({ id: driver.id, status: nextStatus });
      } catch (e) {
        console.warn("Backend update-status pending, updated locally:", e);
      }

      enqueueSnackbar(`Đã ${actionText} tài xế thành công`, { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Lỗi khi cập nhật trạng thái tài xế", { variant: "error" });
    }
  };

  const handleDeleteDriver = async (driver: any) => {
    if (!window.confirm(`Bạn có chắc chắn muốn XÓA tài xế "${driver.name}" khỏi danh sách?`)) {
      return;
    }

    try {
      setDrivers((prev) => prev.filter((d) => d.id !== driver.id));

      const assignedDistricts = driverDistrictsMap[driver.id] || [];
      const updatedMap = { ...driverDistrictsMap };
      delete updatedMap[driver.id];
      setDriverDistrictsMap(updatedMap);
      localStorage.setItem("driver_assigned_districts", JSON.stringify(updatedMap));

      try {
        localStorage.removeItem(`driver_bank_${driver.id}`);
      } catch (e) {}

      // Call backend delete
      try {
        await driverApi.deleteDriver(driver.id);
      } catch (e) {
        console.warn("Backend delete pending, removed locally:", e);
      }

      // Unassign all active orders in districts that were assigned to this deleted driver
      (async () => {
        for (const dCode of assignedDistricts) {
          try {
            const res = await donationApi.getDonations({
              filter: {
                provinceId: "202",
                shippingTypeId: "11123",
                statuses: ["new", "confirm", "processing"],
                districtId: dCode,
              },
              paging: { offset: 0, limit: 100 },
            });
            if (res.data?.code === 0 && res.data?.data?.userDonations) {
              for (const order of res.data.data.userDonations) {
                if (order.driverId === driver.id) {
                  await donationApi.assignDriver({
                    orderId: order.id,
                    driverId: "",
                    driverIncome: 0,
                  });
                }
              }
            }
          } catch (e) {
            console.error("Clean up orders for deleted driver error", e);
          }
        }
      })();

      enqueueSnackbar(`Đã xóa tài xế "${driver.name}" và tự động gỡ toàn bộ đơn hàng liên quan`, { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Lỗi khi xóa tài xế", { variant: "error" });
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <h2>Danh sách tài xế</h2>
        <button 
          onClick={() => setOpenModal(true)}
          style={{ padding: "10px 20px", backgroundColor: "#4CAF50", color: "white", border: "none", borderRadius: 5, cursor: "pointer" }}
        >
          Thêm tài xế
        </button>
      </div>

      <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ width: "100%", minWidth: "850px", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ backgroundColor: "#f2f2f2", textAlign: "left" }}>
            <th style={{ padding: 12, borderBottom: "1px solid #ddd" }}>ID</th>
            <th style={{ padding: 12, borderBottom: "1px solid #ddd" }}>Tên tài xế</th>
            <th style={{ padding: 12, borderBottom: "1px solid #ddd" }}>Số điện thoại (Tài khoản)</th>
            <th style={{ padding: 12, borderBottom: "1px solid #ddd" }}>Tài khoản ngân hàng</th>
            <th style={{ padding: 12, borderBottom: "1px solid #ddd" }}>Khu vực phụ trách (Quận/Huyện)</th>
            <th style={{ padding: 12, borderBottom: "1px solid #ddd" }}>Trạng thái</th>
            <th style={{ padding: 12, borderBottom: "1px solid #ddd", textAlign: "center" }}>Hành động</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} style={{ textAlign: "center", padding: 20 }}><Loading /></td></tr>
          ) : drivers.length === 0 ? (
            <tr><td colSpan={7} style={{ textAlign: "center", padding: 20 }}>Chưa có tài xế nào.</td></tr>
          ) : (
            drivers.map((d: any) => {
              // Read synchronized bank info
              let bankInfo = null;
              try {
                const raw = localStorage.getItem(`driver_bank_${d.id}`);
                if (raw) bankInfo = JSON.parse(raw);
              } catch (e) {}

              const bankDisplay = d.bankAccount
                ? `${d.bankName || "NH"}: ${d.bankAccount} (${d.accountHolder || d.name})`
                : bankInfo
                ? `${bankInfo.bankName || "NH"}: ${bankInfo.bankAccount} (${bankInfo.accountHolder || d.name})`
                : "Chưa cập nhật";

              const isPaused = d.status === "DISABLED";
              const assignedDistrictCodes = driverDistrictsMap[d.id] || [];

              return (
                <tr key={d.id} style={{ backgroundColor: isPaused ? "#fafafa" : "transparent" }}>
                  <td style={{ padding: 12, borderBottom: "1px solid #ddd" }}>{d.id}</td>
                  <td style={{ padding: 12, borderBottom: "1px solid #ddd", fontWeight: "bold" }}>{d.name}</td>
                  <td style={{ padding: 12, borderBottom: "1px solid #ddd" }}>{d.phone}</td>
                  <td style={{ padding: 12, borderBottom: "1px solid #ddd", color: bankDisplay === "Chưa cập nhật" ? "#94a3b8" : "#166534", fontWeight: bankDisplay === "Chưa cập nhật" ? "normal" : "500" }}>
                    {bankDisplay}
                  </td>
                  <td style={{ padding: 12, borderBottom: "1px solid #ddd", maxWidth: 260 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                      {assignedDistrictCodes.length > 0 ? (
                        assignedDistrictCodes.map((code) => {
                          const dist = DISTRICTS.find((item: any) => item.code === code);
                          return (
                            <span
                              key={code}
                              style={{
                                backgroundColor: "#e0f2fe",
                                color: "#0369a1",
                                padding: "2px 8px",
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 500,
                              }}
                            >
                              {dist ? dist.name : code}
                            </span>
                          );
                        })
                      ) : (
                        <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>
                          Chưa phân khu vực
                        </span>
                      )}
                      <button
                        onClick={() => {
                          setSelectedDriverForDistrict(d);
                          setDistrictModalOpen(true);
                        }}
                        style={{
                          marginLeft: 4,
                          padding: "2px 6px",
                          fontSize: 11,
                          backgroundColor: "#f1f5f9",
                          border: "1px solid #cbd5e1",
                          borderRadius: 4,
                          cursor: "pointer",
                          color: "#334155",
                          fontWeight: 600,
                        }}
                        title="Thiết lập quận huyện phụ trách"
                      >
                        ⚙️ Cài đặt
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: 12, borderBottom: "1px solid #ddd" }}>
                    <span style={{ 
                      padding: "4px 8px", 
                      borderRadius: 4, 
                      backgroundColor: isPaused ? "#ffebee" : "#e8f5e9", 
                      color: isPaused ? "#c62828" : "#2e7d32", 
                      fontSize: 12, 
                      fontWeight: "bold" 
                    }}>
                      {isPaused ? "TẠM DỪNG" : "HOẠT ĐỘNG"}
                    </span>
                  </td>
                  <td style={{ padding: 12, borderBottom: "1px solid #ddd", textAlign: "center" }}>
                    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                      <button
                        onClick={() => handleToggleStatus(d)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 4,
                          border: "none",
                          cursor: "pointer",
                          backgroundColor: isPaused ? "#1976d2" : "#ed6c02",
                          color: "#ffffff",
                          fontSize: 12,
                          fontWeight: "500",
                        }}
                      >
                        {isPaused ? "Kích hoạt" : "Tạm dừng"}
                      </button>
                      <button
                        onClick={() => handleDeleteDriver(d)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 4,
                          border: "none",
                          cursor: "pointer",
                          backgroundColor: "#d32f2f",
                          color: "#ffffff",
                          fontSize: 12,
                          fontWeight: "500",
                        }}
                      >
                        Xóa
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      {openModal && (
        <ModalCreateDriver 
          open={openModal} 
          handleClose={() => setOpenModal(false)} 
          refresh={fetchDrivers} 
          partnerId={getPartnerId()} 
        />
      )}

      {districtModalOpen && selectedDriverForDistrict && (
        <ModalAssignDistrict
          open={districtModalOpen}
          handleClose={() => {
            setDistrictModalOpen(false);
            setSelectedDriverForDistrict(null);
          }}
          driver={selectedDriverForDistrict}
          currentDistricts={driverDistrictsMap[selectedDriverForDistrict.id] || []}
          onSave={handleSaveDistricts}
        />
      )}
    </div>
  );
};

export default DriverList;
